import { autoPlacement, autoUpdate, computePosition, Middleware, offset, platform, size, flip } from '@floating-ui/dom'
import { offsetParent } from 'composed-offset-position'
import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './popup.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


type PopupPosition = 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end'


@customElement('cse-popup')
export class CSEPopup extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  private cleanup: ReturnType<typeof autoUpdate> | null = null

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ reflect: true })
  position: PopupPosition = 'top'

  @property({ type: Number })
  distance = 0

  @property({ type: Number })
  skidding = 0

  @property({ type: String })
  sync: 'width' | 'height' | 'both' | null = null

  @property({ type: String })
  override role: string = 'listbox'

  @property({ type: String, attribute: 'auto-size' })
  autoSize: 'horizontal' | 'vertical' | 'both' | null = null

  @property({ type: Number, attribute: 'auto-size-padding' })
  autoSizePadding = 0

  @property({ type: Boolean, attribute: 'auto-position' })
  useAutoPosition: boolean = false

  @property({ reflect: true, attribute: 'current-placement' })
  currentPlacement: PopupPosition = 'top'

  @query('slot[name="anchor"]')
  private anchorSlot!: HTMLSlotElement

  @query('.cse-popup')
  private popup!: HTMLElement

  private get anchorElement(): Element {
    const isElement = (node: Node): node is Element => node instanceof Element
    const assignedElements: Element[] = this.anchorSlot.assignedNodes({ flatten: true }).filter(isElement)
    if (assignedElements.length !== 1) {
      throw new Error('Invalid anchor element: there should be exactly one anchor.')
    }
    return assignedElements[0] as Element
  }

  override async connectedCallback() {
    super.connectedCallback()

    await this.updateComplete
    this.start()
  }

  override disconnectedCallback() {
    this.stop()
  }

  override async updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps)

    if (changedProps.has('active')) {
      if (this.active) {
        this.start()
      } else {
        this.stop()
      }
    }

    if (this.active) {
      await this.updateComplete
      this.reposition()
    }
  }

  private applyStyle(element: HTMLElement, styles: { [key: string]: string | null }) {
    for (const [key, value] of Object.entries(styles)) {
      if (value !== null) {
        element.style.setProperty(key, value)
      } else {
        element.style.removeProperty(key)
      }
    }
  }

  private get offsetMiddleware(): Middleware {
    return offset({ mainAxis: this.distance, crossAxis: this.skidding })
  }

  private get syncMiddleware(): Middleware {
    return size({
      apply: ({ rects }) => {
        const syncWidth = this.sync === 'width' || this.sync === 'both'
        const syncHeight = this.sync === 'height' || this.sync === 'both'
        const width = syncWidth ? `${rects.reference.width}px` : null
        const height = syncHeight ? `${rects.reference.height}px` : null

        this.applyStyle(this.popup, { width: width, height: height })
      },
    })
  }

  private get autoSizeMiddleware(): Middleware {
    return size({
      padding: this.autoSizePadding,
      apply: ({ availableWidth, availableHeight }) => {
        const autoSizeWidth = this.autoSize === 'horizontal' || this.autoSize === 'both'
        const autoSizeHeight = this.autoSize === 'vertical' || this.autoSize === 'both'
        const width = autoSizeWidth ? `${availableWidth}px` : null
        const height = autoSizeHeight ? `${availableHeight}px` : null

        this.applyStyle(this, { '--cse-popup-auto-size-available-width': width, '--cse-popup-auto-size-available-height': height })
      },
    })
  }

  private start() {
    if (!this.anchorElement) { return }

    this.cleanup = autoUpdate(this.anchorElement, this.popup, () => {
      this.reposition()
    })
  }

  private stop() {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
  }

  private async reposition() {
    if (!this.active) { return }

    const middleware: Middleware[] = [this.offsetMiddleware]

    if (this.sync) {
      middleware.push(this.syncMiddleware)
    } else {
      this.applyStyle(this.popup, { width: null, height: null })
    }

    if (this.autoSize) {
      middleware.push(this.autoSizeMiddleware)
    } else {
      this.applyStyle(this, { '--cse-popup-auto-size-available-width': null, '--cse-popup-auto-size-available-height': null })
    }

    const getOffsetParent = (element: Element) => platform.getOffsetParent(element, offsetParent)

    if (this.useAutoPosition) {
      middleware.push(autoPlacement())
    } else {
      middleware.push(flip())
    }

    const options = {
      placement: this.position,
      middleware: middleware,
      platform: {
        ...platform,
        getOffsetParent,
      },
    }

    const { x, y, placement } = await computePosition(this.anchorElement, this.popup, options)

    this.currentPlacement = placement as PopupPosition
    this.applyStyle(this.popup, { left: `${x}px`, top: `${y}px` })
  }

  protected override render(): TemplateResult {
    const classes: { [className: string]: boolean } = {
      'cse-popup': true,
      'active': this.active,
    }

    return html`
      <slot name="anchor"></slot>
      <div class=${classMap(classes)} role=${this.role}>
        <slot></slot>
      </div>
    `
  }

}


declare global {
  interface HTMLElementTagNameMap {
    'cse-popup': CSEPopup
  }
}
